import { useEffect, useRef, useState } from "react";
import type * as maplibreglType from 'maplibre-gl';
import './IndexMap.css';
import { useAtomValue } from "jotai";
import { selectedBaseMapAtom } from "../../atoms";
import { IndexMapProps } from "../../types/components/indexMap";
import MAP_STYLE from '../../assets/style.json';

const useIndexMap: React.FC<IndexMapProps> = ({ mainMap }) => {

    const mapContainer = useRef<HTMLDivElement>(null);
    const selectedBaseMap = useAtomValue(selectedBaseMapAtom);
    const [indexMapSize, setindexMapSize] = useState<{w: string, h: string} | undefined>(undefined);
    const [boxSize, setBoxSize] = useState<{w: string, h: string} | undefined>(undefined);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
    const dragOffset = useRef({ x: 0, y: 0 });
    const overviewMapRef = useRef<maplibreglType.Map | null>(null);


    /* ***************************
     * 索引図の初期化
     * ****************************/
    useEffect(() => {
        if (!mainMap || !selectedBaseMap) { return; }
        const mainMapWidth = mainMap.getCanvas().clientWidth;
        const mainMapHeight = mainMap.getCanvas().clientHeight;

        // boxのサイズをmainMapのサイズの1/9に設定
        const boxWidth = mainMapWidth / 11;
        const boxHeight = mainMapHeight / 11;

        setBoxSize({
            w: boxWidth + 'px', 
            h: boxHeight + 'px'
        });

        // index-mapのサイズをmainMapのサイズの1/7に設定
        const indexMapWidth = mainMapWidth / 6;
        const indexMapHeight = mainMapHeight / 6;

        setindexMapSize({
            w: indexMapWidth + 'px', 
            h: indexMapHeight + 'px'
        });

        // ズームレベルの比率を計算
        const zoomRatio = Math.log2(9);

        // サブマップ（索引図）の初期化
        const overviewMap: maplibreglType.Map = new window.geolonia.Map({
            container: mapContainer.current!,
            style: MAP_STYLE as maplibreglType.StyleSpecification,
            center: mainMap.getCenter(),
            zoom: mainMap.getZoom() - zoomRatio
        });

        overviewMapRef.current = overviewMap;

        // 初期位置をoverviewMapの中心に設定
        overviewMap.on('load', () => {
            const bounds = mainMap.getBounds();
            overviewMap.fitBounds(bounds, { duration: 0 });
            const overviewMapWidth = overviewMap.getCanvas().clientWidth;
            const overviewMapHeight = overviewMap.getCanvas().clientHeight;
            const initialX = (overviewMapWidth - boxWidth) / 2;
            const initialY = (overviewMapHeight - boxHeight) / 2;

            setPosition({ x: initialX, y: initialY });
            setInitialPosition({ x: initialX, y: initialY });
        });

        // メインマップの移動に合わせて索引図を更新
        const updateOverviewMap = () => {
            const bounds = mainMap.getBounds();
            overviewMap.setCenter({
                lng: (bounds._ne.lng + bounds._sw.lng) / 2, 
                lat: (bounds._ne.lat + bounds._sw.lat) / 2 
            });
            overviewMap.setZoom(mainMap.getZoom() - zoomRatio);
        };

        mainMap.on('moveend', updateOverviewMap);
        mainMap.on('zoomend', updateOverviewMap);

        // クリーンアップ
        return () => {
            mainMap.off('moveend', updateOverviewMap);
            mainMap.off('zoomend', updateOverviewMap);
        };

    }, [mainMap, selectedBaseMap]);


    /* ***************************
     * ドラッグ開始時の処理
     * ****************************/
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        dragOffset.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
    };


    /* ***************************
     * ドラッグ中の処理
     * ***************************/
    useEffect(() => {

        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                setPosition({
                    x: e.clientX - dragOffset.current.x,
                    y: e.clientY - dragOffset.current.y
                });
            }
        };

        const handleMouseUp = () => {
            // boxの位置とサイズを取得してoverviewMapの座標系に変換
            if (overviewMapRef.current && mainMap && isDragging) {
                const boxTopLeft = overviewMapRef.current.unproject([position.x, position.y]);
                const boxBottomRight = overviewMapRef.current.unproject([position.x + parseInt(boxSize?.w || '0'), position.y + parseInt(boxSize?.h || '0')]);

                mainMap.setCenter({ 
                    lng: (boxTopLeft.lng + boxBottomRight.lng) / 2, 
                    lat: (boxTopLeft.lat + boxBottomRight.lat) / 2 
                });

                // boxの位置を初期の中心の位置に移動
                setPosition(initialPosition);
            }
            setIsDragging(false);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [boxSize?.h, boxSize?.w, initialPosition, isDragging, mainMap, position.x, position.y]);

    return (
        <div className='index-map-container'>
            <div className='index-map-inner'>
                <div
                    className='index-map'
                    ref={mapContainer}
                    data-lang="ja"
                    data-gesture-handling="off"
                    data-navigation-control="off"
                    data-scale-control="off"
                    style={{
                        width: indexMapSize ? indexMapSize.w : '0', 
                        height: indexMapSize ? indexMapSize.h : '0'
                    }}
                ></div>
                <div 
                    className="box"
                    onMouseDown={handleMouseDown}
                    style={{
                        width: boxSize ? boxSize.w : '0', 
                        height: boxSize ? boxSize.h : '0',
                        top: position.y,
                        left: position.x,
                        position: 'absolute',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)'
                    }}
                ></div>
            </div>
        </div>
    );
};

export default useIndexMap;